import { Box, TextField, Collapse } from '@mui/material'
import HelpOutlineIcon from '@mui/icons-material/HelpOutline'
import { LegalEntityParty } from '@types'
import { entityNeedsTrusteeName } from 'utils/trustEntityUtils'

interface TrusteeNameEditorProps {
  entity: LegalEntityParty | null
  trusteeName: string
  onTrusteeNameChange: (name: string) => void
  disabled?: boolean
  error?: string
}

/**
 * TrusteeNameEditor - A component for entering the trustee name for trust beneficiaries.
 * 
 * This component is displayed when the backend indicates NEEDS_TRUSTEE stipulation.
 * 
 * The trustee name is prefixed to the trust name for display purposes in:
 * - UI blotter and details pages
 * - PRB emails
 * - Notifications
 * - Certificate (suffixed with trust ABN)
 */
const TrusteeNameEditor = ({
  entity,
  trusteeName,
  onTrusteeNameChange,
  disabled = false,
  error
}: TrusteeNameEditorProps) => {
  const needsTrustee = entityNeedsTrusteeName(entity)
  
  if (!needsTrustee) {
    return null
  }
  
  return (
    <Collapse in={needsTrustee}>
      <Box sx={{ mt: 2 }}>
        <TextField
          fullWidth
          label="Trustee Name (Optional)"
          placeholder="e.g., John Smith, ABC Pty Ltd"
          value={trusteeName}
          onChange={(e) => onTrusteeNameChange(e.target.value)}
          disabled={disabled}
          error={!!error}
          helperText={
            error ? (
              error
            ) : (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <HelpOutlineIcon sx={{ fontSize: 16 }} />
                <span>Helpful Hint: Use this field to amend the beneficiary name if required</span>
              </Box>
            )
          }
          autoComplete="off"
          InputLabelProps={{ shrink: true }}
          sx={{ width: '98%' }}
        />
      </Box>
    </Collapse>
  )
}

export default TrusteeNameEditor
